import { Grid, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import {
  DRPConfirmationConstants,
  findAPartnerRepairShopBtn,
  GenericModalTopMargin,
  goToClaimsBtn,
  skipRepairShopBtn,
} from '../../../Constants/Constants';
import getCopyById from '../../../copy/copyMapper';
import { Features } from '../../../types/type';
import { getNextView, getStagesInOrder } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import GenericModal from '../../shared/GenericModal/GenericModal';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function RepairServicesHome(props: any) {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const [disclaimerOpen, setDisclaimerOpen] = React.useState(false);
  const opco = useSelector(getServicingOpCo);
  const repairInfo = useAppSelector((state) => state.services.eligibleRepairService);
  const disclaimers = repairInfo.serviceRequestDisclaimer;
  const partnerId = useSelector(getPartnerId);
  const copy = getCopyById(opco, partnerId);

  const headerContent = `Your claim is eligible for our ${copy.repairServiceName}`;

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== Features.REPAIR && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  function handleNext() {
    navigate('/repair-selection');
  }

  function getConsolidatedDescription(disclaimers: any[]) {
    let consolidatedDescription = '';
    disclaimers.forEach((element) => {
      consolidatedDescription = consolidatedDescription + element.disclaimerDescription + '\n\n';
    });
    return consolidatedDescription;
  }

  function handleSkip() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.REPAIR
          ? {
              ...stage,
              completed: false,
              viewActive: false,
              showProgress: true,
              skipped: true,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    navigate('/claims-overview', {
      state: {
        isClaimRefreshNeeded: true,
      },
    });
  }
  function handleCloseButtonClick() {
    setDisclaimerOpen(false);
  }

  function handleConfirmation() {
    handleNext();
  }
  const disclaimerModal = (
    <GenericModal
      title={DRPConfirmationConstants.TITLE}
      description={getConsolidatedDescription(disclaimers)}
      primaryButtonText={DRPConfirmationConstants.PRIMARY_BUTTON_TEXT}
      secondaryButtonText={DRPConfirmationConstants.SECONDARY_BUTTON_TEXT}
      alertBoxStyle={theme.custom.styles.genericModalAlertBoxStyle}
      verticalPositionForMobile={GenericModalTopMargin}
      theme={theme}
      closeButtonActionHandler={handleCloseButtonClick}
      primaryButtonActionHandler={handleConfirmation}
      secondaryButtonActionHandler={handleCloseButtonClick}
    ></GenericModal>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {disclaimerOpen && disclaimerModal}
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      <PropertyServiceInfo
        headerContent={headerContent}
        infoContent={
          <>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                Our {copy.repairServiceName} allows you to find a fast and trusted partner repair shop to fix your
                vehicle with a <b>lifetime workmanship guarantee.</b> It's reliable, quick and convenient - we'll handle
                the estimate and the billing, and you just pay your deductible.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                Don't want to select a repair shop now? You can choose a shop later from the <b>Claims</b> tab.
              </Typography>
            </Grid>
          </>
        }
      ></PropertyServiceInfo>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonContent={findAPartnerRepairShopBtn}
            handleNext={() => setDisclaimerOpen(true)}
            backButton={false}
            skipButton={true}
            skipButtonContent={skipRepairShopBtn}
            handleSkip={handleSkip}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
